import jsPDF from 'jspdf';
import { resolveMediaUrl } from './mediaUrl';

interface PdfQuestion {
  number: number;
  text: string;
  options: { letter: string; text: string }[];
  correct_answer: string;
  explanation?: string | null;
  topic?: string | null;
  difficulty?: string;
  images?: string[] | null;
}

/** Fetch an image URL and return a base64 data-URL, or null on failure. */
async function fetchImageAsBase64(src: string): Promise<{ dataUrl: string; format: string } | null> {
  try {
    const res = await fetch(src);
    if (!res.ok) return null;
    const blob = await res.blob();
    const format = blob.type.includes('png') ? 'PNG' : blob.type.includes('gif') ? 'GIF' : 'JPEG';
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve({ dataUrl: reader.result as string, format });
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Generate a printable PDF of questions with an optional answer key.
 * Now async to support image embedding.
 */
export async function exportQuestionsPdf(
  questions: PdfQuestion[],
  title = 'CloudMaster Practice Questions',
  includeAnswerKey = true,
): Promise<void> {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 15;
  const maxW = pageW - 2 * margin;
  let y = margin;

  const addPage = () => {
    doc.addPage();
    y = margin;
  };

  const ensureSpace = (need: number) => {
    if (y + need > pageH - margin) addPage();
  };

  // ── Title page ──────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text(title, pageW / 2, 50, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.text(`${questions.length} question${questions.length !== 1 ? 's' : ''}`, pageW / 2, 62, { align: 'center' });
  doc.text(new Date().toLocaleDateString(), pageW / 2, 70, { align: 'center' });

  addPage();

  // ── Questions ───────────────────────────────────────────────────────
  for (const q of questions) {
    ensureSpace(40);

    // Question text
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    const qLines = doc.splitTextToSize(`Q${q.number}. ${q.text}`, maxW);
    doc.text(qLines, margin, y);
    y += qLines.length * 5 + 3;

    // Embedded images
    if (q.images && q.images.length > 0) {
      for (const rawSrc of q.images) {
        const resolvedSrc = resolveMediaUrl(rawSrc);
        if (!resolvedSrc) continue;
        const imgData = await fetchImageAsBase64(resolvedSrc);
        if (!imgData) continue;

        // Determine display size — max width = maxW, max height = 80mm
        const tempImg = new Image();
        await new Promise<void>((res) => {
          tempImg.onload = () => res();
          tempImg.onerror = () => res();
          tempImg.src = imgData.dataUrl;
        });

        const nativeW = tempImg.naturalWidth || 400;
        const nativeH = tempImg.naturalHeight || 300;
        const aspectRatio = nativeW / nativeH;
        const displayW = Math.min(maxW, 140);
        const displayH = Math.min(displayW / aspectRatio, 80);

        ensureSpace(displayH + 6);
        doc.addImage(imgData.dataUrl, imgData.format, margin, y, displayW, displayH);
        y += displayH + 5;
      }
    }

    // Options
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    for (const opt of q.options) {
      ensureSpace(8);
      const optLines = doc.splitTextToSize(`${opt.letter}. ${opt.text}`, maxW - 8);
      doc.text(optLines, margin + 5, y);
      y += optLines.length * 4.5 + 2;
    }

    y += 6;
  }

  // ── Answer Key ──────────────────────────────────────────────────────
  if (includeAnswerKey) {
    addPage();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Answer Key', pageW / 2, y, { align: 'center' });
    y += 12;

    doc.setFontSize(10);
    for (const q of questions) {
      ensureSpace(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`Q${q.number}: ${q.correct_answer}`, margin, y);

      if (q.explanation) {
        doc.setFont('helvetica', 'normal');
        const exLines = doc.splitTextToSize(q.explanation, maxW - 20);
        doc.text(exLines.slice(0, 4), margin + 15, y);
        y += Math.min(exLines.length, 4) * 4.5 + 4;
      } else {
        y += 6;
      }
    }
  }

  doc.save(`${title.replace(/\s+/g, '_')}.pdf`);
}

